// ============================================
// UI STORE — Zustand
// Sidebar collapse, dark mode, loading states
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  isSidebarCollapsed: boolean;
  isMobileSidebarOpen: boolean;
  isDarkMode: boolean;
  unlockedPaths: Record<string, boolean>;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  toggleTheme: () => void;
  setDarkMode: (value: boolean) => void;
  unlockPath: (path: string) => void;
  lockPath: (path: string) => void;
  theme?: 'light' | 'dark';
  setTheme?: (theme: 'light' | 'dark') => void;
}

const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      isSidebarCollapsed: false,
      isMobileSidebarOpen: false,
      isDarkMode: true,
      unlockedPaths: {},

      toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

      toggleMobileSidebar: () =>
        set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen })),

      closeMobileSidebar: () => set({ isMobileSidebarOpen: false }),

      toggleTheme: () =>
        set((state) => {
          const newMode = !state.isDarkMode;
          if (newMode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
          return { isDarkMode: newMode };
        }),

      setDarkMode: (value) => {
        if (value) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        set({ isDarkMode: value });
      },

      unlockPath: (path) =>
        set((state) => ({
          unlockedPaths: { ...state.unlockedPaths, [path]: true },
        })),

      lockPath: (path) =>
        set((state) => ({
          unlockedPaths: { ...state.unlockedPaths, [path]: false },
        })),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        isDarkMode: state.isDarkMode,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply dark mode on app load based on stored preference or default
        if (state?.isDarkMode) {
          document.documentElement.classList.add('dark');
        } else if (state?.isDarkMode === false) {
          document.documentElement.classList.remove('dark');
        }
      },
    },
  ),
);

export default useUIStore;
